import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Button,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
} from '@mui/material';
import { Search as SearchIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api from '../api/api';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

const ConfiguracionDomicilio = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [originalConfigs, setOriginalConfigs] = useState([]);
  const [sucursales, setSucursales] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterNoDate, setFilterNoDate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sucursalFiltro, setSucursalFiltro] = useState('');
  const [sucursalUsuario, setSucursalUsuario] = useState(null);

  // 1. Efecto para leer la sucursal del usuario autenticado desde el Token JWT
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const sucursalId = payload.id_sucursal || payload.sucursal_id || payload.idSucursal || null;
        if (sucursalId) {
          setSucursalUsuario(String(sucursalId));
          setSucursalFiltro(String(sucursalId)); // Inicializa con su sucursal por defecto
        }
      } catch (err) {
        console.error('Error al leer sucursal del token:', err);
      }
    }
  }, []);

  // 2. Carga unificada usando el nuevo endpoint optimizado de Inicialización
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await api.get('/logistica_domicilio/init_dashboard');
        
        // Las configuraciones ya traen inyectados nombre_cliente, nombre_zona, nombre_sub_zona y nombre_sucursal
        const dataConfigs = response.data.configuraciones || [];
        setOriginalConfigs(dataConfigs);
        setConfigs(dataConfigs);
        
        setSucursales(response.data.sucursales || []);
      } catch (err) {
        console.error(err);
        setError('Error al cargar el dashboard de configuraciones de domicilio');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 3. Sistema de Filtrado Ultra Rápido (Actúa directo sobre los campos planos del SQL)
  useEffect(() => {
    if (originalConfigs.length === 0) {
      setConfigs([]);
      return;
    }

    setPage(0);
    let resultado = [...originalConfigs];

    // Filtro por Sucursal asignada al Cliente
    if (sucursalFiltro && sucursalFiltro !== "") {
      resultado = resultado.filter((c) => String(c.id_sucursal) === String(sucursalFiltro));
    }

    // Buscador global inteligente
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      resultado = resultado.filter((c) => {
        const nombreCliente = c.nombre_cliente || `Cliente ${c.id_cliente}`;
        const nombreZona = c.nombre_zona || '';
        const nombreSubZona = c.nombre_sub_zona || '';
        const frecuencia = c.frecuencia || '';
        const hojaServicio = c.hoja_servicio_fija || '';

        return (
          nombreCliente.toLowerCase().includes(term) ||
          frecuencia.toLowerCase().includes(term) ||
          hojaServicio.toLowerCase().includes(term) ||
          nombreZona.toLowerCase().includes(term) ||
          nombreSubZona.toLowerCase().includes(term)
        );
      });
    }

    // Filtro de Atención Requerida (Sin fechas estipuladas)
    if (filterNoDate) {
      resultado = resultado.filter(
        (c) => !c.ultima_visita_realizada || !c.posponer_hasta
      );
    }

    setConfigs(resultado);
  }, [searchTerm, originalConfigs, filterNoDate, sucursalFiltro]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && !configs.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        
        {/* Encabezado */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/clientes')} 
            sx={{ mr: 2 }}
          >
            Regresar
          </Button>
          <Typography variant="h4" sx={{ mb: 0 }}>
            Configuraciones de Domicilios Recurrentes
          </Typography>
        </Box>

        {/* Barra de Filtros y Buscador */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Buscador Global */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar por cliente, frecuencia, servicio, zona o subzona..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 240 }}
          />

          {/* Selector de Sucursal */}
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Sucursal</InputLabel>
            <Select
              value={sucursalFiltro}
              label="Sucursal"
              onChange={(e) => setSucursalFiltro(e.target.value)}
            >
              <MenuItem value=""><em>Todas las Sucursales</em></MenuItem>
              {sucursales.map((s) => (
                <MenuItem key={s.id_sucursal} value={String(s.id_sucursal)}>
                  {s.nombre_sucursal}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Indicador de Sucursal Asignada */}
          {sucursalFiltro && sucursalUsuario && sucursalFiltro === sucursalUsuario && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (tu sucursal por defecto)
            </Typography>
          )}

          {/* Checkbox de Atención Requerida */}
          <FormControlLabel
            sx={{ minWidth: '280px' }}
            control = {
              <Checkbox
                checked={filterNoDate}
                onChange={(e) => setFilterNoDate(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                Atención Requerida (Sin fechas)
              </Typography>
            }
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tabla de Resultados */}
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Zona</TableCell>
                <TableCell>Subzona</TableCell>
                <TableCell>Sucursal</TableCell> {/* ← Nueva columna añadida con éxito */}
                <TableCell>Frecuencia</TableCell>
                <TableCell>Día Preferido</TableCell>
                <TableCell>Última Visita</TableCell>
                <TableCell>Pospuesto Hasta</TableCell>
                <TableCell>Servicio fijo</TableCell>
                <TableCell>Activo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <Typography variant="body1" color="text.secondary">
                      {sucursalFiltro
                        ? `No se encontraron configuraciones para la sucursal seleccionada`
                        : searchTerm || filterNoDate
                        ? "No hay resultados con los filtros actuales"
                        : "No hay configuraciones de domicilios recurrentes registradas"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                configs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((config) => (
                    <TableRow key={config.id_config} hover>
                      <TableCell>{config.id_config}</TableCell>

                      {/* Nombre del Cliente */}
                      <TableCell>
                        <Button
                          size="small"
                          color="primary"
                          sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            textAlign: 'left',
                            padding: 0,
                            minWidth: 'auto',
                          }}
                          onClick={() => window.open(`/clientes/${config.id_cliente}`, '_blank')}
                        >
                          {config.nombre_cliente || `Cliente ${config.id_cliente}`}
                        </Button>
                      </TableCell>

                      {/* Nombre de la Zona */}
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            bgcolor: '#e8f5e9',
                            color: '#2e7d32',
                            px: 1.5,
                            py: 0.4,
                            borderRadius: 1,
                            fontSize: '0.85rem',
                            fontWeight: 'medium',
                            border: '1px solid #a5d6a7',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {config.nombre_zona || '—'}
                        </Box>
                      </TableCell>

                      {/* Nombre de la Subzona */}
                      <TableCell>
                        {config.nombre_sub_zona || '—'}
                      </TableCell>

                      {/* Nombre de la Sucursal */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                          {config.nombre_sucursal || '—'}
                        </Typography>
                      </TableCell>

                      {/* Frecuencia */}
                      <TableCell>{config.frecuencia}</TableCell>

                      {/* Día Preferido */}
                      <TableCell>
                        {config.dia_preferido
                          ? ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'][config.dia_preferido - 1]
                          : '—'}
                      </TableCell>

                      {/* Última Visita Realizada */}
                      <TableCell>
                        {config.ultima_visita_realizada ? (
                          dayjs(config.ultima_visita_realizada).format('DD MMMM YYYY')
                        ) : (
                          <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                            Pendiente
                          </Typography>
                        )}
                      </TableCell>

                      {/* Pospuesto Hasta */}
                      <TableCell>
                        {config.posponer_hasta ? (
                          <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                            {dayjs(config.posponer_hasta).format('DD MMMM YYYY')}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      {/* Hoja de Servicio Fija */}
                      <TableCell>
                        {config.hoja_servicio_fija ? `${config.hoja_servicio_fija.substring(0, 30)}...` : '—'}
                      </TableCell>

                      {/* Estatus Activo */}
                      <TableCell>{config.activo ? 'Sí' : 'No'}</TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={configs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Container>
    </Box>
  );
};

export default ConfiguracionDomicilio;